import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import * as am4core from "@amcharts/amcharts4/core";
import { injectIntl } from "react-intl";

import CampaignDetailView from "views/Campaign/Detail";
import CampaignService from "api/Campaign";
import AccountService from "api/Account";
import UserService from "api/User";
import SettingsActions from "store/reducers/Settings";
import AccountActions from "store/reducers/Account";
import UserActions from "store/reducers/User";
import { useDelete, useFetch } from "hooks";
import { handlePromise } from "utils";

const colorsGraph = {
  communicating: "#4C7CB7",
  hangup: "#F9C162",
  ignored: "#6A8FC3",
  listened: "#F2A858",
  pressed: "#FD9900",
  rejected: "#93A9D0",
  farewellListened: "#F2A858",
  optionListened: "#F2A858",
  introListened: "#F2A858",
  error: "#3D6698",
  nonExistent: "#254061",
};

const colorsKeyGraph = {
  digit0: "#FD9900",
  digit1: "#FD9900",
  digit2: "#FD9900",
  digit3: "#FD9900",
  digit4: "#FD9900",
  digit5: "#FD9900",
  digit6: "#FD9900",
  digit7: "#FD9900",
  digit8: "#FD9900",
  digit9: "#FD9900",
};

const CampaignDetail = ({
  parentPath,
  match,
  history,
  intl,
  updateUser,
  updateAccountInfo,
  toggleErrorAlert,
  toggleLoading,
  user,
}) => {
  const entityId = match.params.id;
  const [dataSubmitted, setDataSubmitted] = useState(new Date().valueOf());
  const [chartData, setChartData] = useState([]);
  const [chartKeyData, setChartKeyData] = useState([]);

  const [data, dataLoading] = useFetch({
    initialState: {},
    promise: () => CampaignService.getCampaign(entityId),
    toggleErrorAlert,
    conditional: !!entityId,
    deps: [entityId, dataSubmitted],
  });
  const [tempData, setTempData] = useState({});

  const createChartDatas = useCallback(
    (respData) => {
      am4core.addLicense("CH63305913");
      am4core.options.commercialLicense = true;
      if (respData && respData.callsData) {
        const { callsData } = respData;
        const { pressedData } = callsData;
        const chData = [];

        Object.keys(callsData).forEach((a) => {
          if (a !== "pressedData" && callsData[a]) {
            chData.push({
              id: a.toUpperCase(),
              action: intl.formatMessage({ id: a }),
              color: am4core.color(colorsGraph[a] || "#808080"),
              people: callsData[a],
            });
          }
        });

        setChartData(chData);

        if (pressedData) {
          const chKeyData = [];
          Object.keys(pressedData).forEach((a) => {
            if (pressedData[a]) {
              const key = a.replace("digit", "");
              const keyName = intl.formatMessage({ id: "press" });
              chKeyData.push({
                id: `${keyName} ${key}`,
                action: `${keyName} ${key}`,
                color: am4core.color(colorsKeyGraph[a] || "#808080"),
                people: pressedData[a],
              });
            }
          });
          setChartKeyData(chKeyData);
        }
      }
    },
    [setChartData, setChartKeyData, intl]
  );

  const fetchData = async () => {
    const result = await CampaignService.getCampaign(entityId);
    if (result.status === 200) {
      setTempData(result.data);
    }
  };

  useEffect(() => {
    let interval = null;

    const getData = async () => {
      if (data.status === "execution") {
        const [, response, responseData] = await handlePromise(
          CampaignService.getCampaignLogs(data.id, true)
        );
        if (response.ok) {
          createChartDatas(responseData, true);
        }

        if (!interval) {
          interval = setInterval(() => {
            getData();
            fetchData();
          }, 5000);
        }
        if (responseData.status !== "execution") {
          //  if ( interval ) clearInterval( interval );
          setDataSubmitted(new Date().valueOf());
        }
      } else if (data.status === "finished" || data.status === "stopped") {
        // if ( interval ) clearInterval( interval );
        if (!interval) {
          interval = setInterval(() => {
            getData();
            fetchData();
          }, 15000);
        }
        const [, response, responseData] = await handlePromise(
          CampaignService.getCampaignLogs(data.id, false)
        );
        if (response.ok) {
          createChartDatas(responseData);
        }
      } else {
        if (!interval) {
          interval = setInterval(() => {
            getData();
            fetchData();
          }, 5000);
        }
        const [, response, responseData] = await handlePromise(
          CampaignService.getCampaignLogs(data.id, false)
        );
        if (response.ok) {
          createChartDatas(responseData);
        }
      }
    };
    if (data.id) {
      getData();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [data.id, data.status, createChartDatas]); //eslint-disable-line

  function hasNumber(myString) {
    return /\d/.test(myString);
  }

  const exportData = useCallback(
    async (action) => {
      const filters = {};
      if (action && action.id) {
        // if (
        //   action.id !== "ANSWER" &&
        //   action.id !== "PRESS" &&
        //   action.id !== "NOANSWER" &&
        //   action.id !== "ERROR"
        // ) {
        //   filters.digits = action.id;
        // } else {
        //   filters.status = action.id;
        // }
        // debugger
        if (hasNumber(action.id)) {
          filters.digits = action.id.split(" ")[1];
        } else {
          if (action.id == "NONEXISTENT") {
            filters.status = "NON_EXISTENT";
          } else if(action.id == "HANGUP") {
            filters.status = "HUNG_UP";
          } else {
            filters.status = action.id;
          }
        }
      }
      toggleLoading(true);
      const [errors, response, responseData] = await handlePromise(
        CampaignService.exportExcel(entityId, filters)
      );
      toggleLoading(false);
      if (!response.ok) return toggleErrorAlert(errors);
      window.open(responseData.uri);
    },
    [entityId, toggleLoading, toggleErrorAlert]
  );

  const deleteEntity = useDelete({
    promise: () => CampaignService.deleteCampaign(entityId),
    callback: () => history.push("/out/campaigns"),
    toggleLoading,
    toggleErrorAlert,
  });

  const changeStatus = useCallback(
    async (status) => {
      toggleLoading(true);
      const [errors, response] = await handlePromise(
        CampaignService.updateCampaign(entityId, { status })
      );
      if (!response.ok) {
        toggleLoading(false);
        return toggleErrorAlert(errors);
      }

      const [, accountResponse, accountResponseData] = await handlePromise(
        AccountService.getMyAccount()
      );
      if (accountResponse.ok) updateAccountInfo(accountResponseData);

      const [, userResponse, userResponseData] = await handlePromise(
        UserService.getUser()
      );
      if (userResponse.ok) updateUser(userResponseData);

      toggleLoading(false);
      setDataSubmitted(new Date().valueOf());
    },
    [entityId, updateUser, updateAccountInfo, toggleLoading, toggleErrorAlert]
  );


  return (
    <CampaignDetailView
      data={Object.keys(tempData).length ? tempData : data}
      chartData={chartData}
      chartKeyData={chartKeyData}
      parentPath={parentPath}
      isLoading={dataLoading}
      onExport={exportData}
      onDelete={deleteEntity}
      onChangeStatus={changeStatus}
      userData={user}
    />
  );
};

const mapStateToProps = ({ user }) => ({
  user,
});

const mapDispatchToProps = {
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  toggleLoading: SettingsActions.toggleLoading,
  updateAccountInfo: AccountActions.updateInfo,
  updateUser: UserActions.updateUser,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(CampaignDetail));
